import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  acquireCallSignalingSocket,
  callAPI,
  getCallSignalingSocket,
  isDuplicateCallSocketClose,
  isRateLimitedCallSocketClose,
  releaseCallSignalingSocket,
  resetCallSocketGuard,
  setCallSocketLockOwner,
} from './utils/callApi';
import {
  acquireLocalAudio,
  acquireScreenShare,
  attachLocalAudio,
  attachOrReplaceScreenAudioTrack,
  attachOrReplaceVideoTrack,
  cacheIceServers,
  canShareScreen,
  clearCachedIceServers,
  clearScreenSenders,
  closePeerConnection,
  createPeerConnection,
  createMicLevelMonitor,
  createRemoteAudioPlayer,
  createCallAudioMixer,
  replaceOutgoingMicTrack,
  getCachedIceServers,
  normalizeIceServers,
  prepareScreenAudioTrack,
  readNoiseSuppressionPreference,
  stopMediaStream,
  writeNoiseSuppressionPreference,
} from './utils/webrtcCall';
import {
  anyMeshConnected,
  attachStreamTracks,
  closeAllMeshPeers,
  closeMeshPeer,
  toSessionDescription,
} from './utils/groupMesh';
import { useSystemWebSocket } from './SystemWebSocketContext';

const VoiceCallContext = createContext(null);

const CALL_STATES = {
  IDLE: 'idle',
  OUTGOING: 'outgoing',
  INCOMING: 'incoming',
  CONNECTING: 'connecting',
  ACTIVE: 'active',
};

const OUTGOING_RING_TIMEOUT_MS = 50000;
const NEGOTIATION_TIMEOUT_MS = 90000;
const PEER_JOIN_WAIT_MS = 8000;
const OFFER_RETRY_MS = 2000;
const OFFER_RETRY_COUNT = 3;

const logCallEvent = (event, detail = {}) => {
  console.info('[call]', event, detail);
};

const isMediaConnected = (pc) => {
  if (!pc) {
    return false;
  }
  return pc.connectionState === 'connected'
    || pc.iceConnectionState === 'connected'
    || pc.iceConnectionState === 'completed';
};

const withTimeout = (promise, timeoutMs, message) => new Promise((resolve, reject) => {
  const timer = window.setTimeout(() => {
    reject(new Error(message));
  }, timeoutMs);
  promise
    .then((value) => {
      window.clearTimeout(timer);
      resolve(value);
    })
    .catch((error) => {
      window.clearTimeout(timer);
      reject(error);
    });
});

export const VoiceCallProvider = ({ children }) => {
  const { subscribe } = useSystemWebSocket();
  const [callState, setCallState] = useState(CALL_STATES.IDLE);
  const [activeCall, setActiveCall] = useState(null);
  const [remoteAudioReady, setRemoteAudioReady] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [noiseSuppression, setNoiseSuppressionState] = useState(() => readNoiseSuppressionPreference());
  const [micLevel, setMicLevel] = useState(0);
  const [peerMicLevel, setPeerMicLevel] = useState(0);
  const [callProfiles, setCallProfiles] = useState({
    selfName: '',
    selfId: '',
    selfAvatarUrl: null,
    peerAvatarUrl: null,
    peerName: null,
  });
  const [activeGroupRooms, setActiveGroupRooms] = useState({});
  const [groupParticipants, setGroupParticipants] = useState([]);
  const [remotePeerStreams, setRemotePeerStreams] = useState({});
  const [peerVolume, setPeerVolume] = useState(1);
  const [screenVolume, setScreenVolume] = useState(1);
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [screenShareHasAudio, setScreenShareHasAudio] = useState(false);
  const [localScreenStream, setLocalScreenStream] = useState(null);
  const [remoteScreenStream, setRemoteScreenStream] = useState(null);
  const [remoteScreenPeerId, setRemoteScreenPeerId] = useState(null);

  const pcRef = useRef(null);
  const pcsRef = useRef(new Map());
  const pendingIceByPeerRef = useRef(new Map());
  const remotePeerStreamsRef = useRef({});
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const screenMixerRef = useRef(null);
  const screenAudioMixedRef = useRef(false);
  const micPipelineRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const remoteAudioPlayerRef = useRef(null);
  const roleRef = useRef(null);
  const callKindRef = useRef('dm');
  const activeCallRef = useRef(null);
  const callStateRef = useRef(callState);
  const remoteAudioReadyRef = useRef(false);
  const teardownRef = useRef(false);
  const negotiatingRef = useRef(false);
  const acceptingRef = useRef(false);
  const preconnectingRef = useRef(null);
  const micMonitorRef = useRef(null);
  const isMutedRef = useRef(false);
  const noiseSuppressionRef = useRef(noiseSuppression);
  const sendSignalRef = useRef(null);
  const handleSignalMessageRef = useRef(null);
  const lastAudioLevelSentRef = useRef(0);
  const lastAudioLevelValueRef = useRef(-1);
  const pendingIceRef = useRef([]);
  const pendingSignalsRef = useRef([]);
  const signalChainRef = useRef(Promise.resolve());
  const answerReceivedRef = useRef(false);
  const peerJoinedRef = useRef(false);
  const peerJoinedWaitersRef = useRef([]);
  const localIceCandidatesRef = useRef([]);
  const makingOfferRef = useRef(false);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  useEffect(() => {
    remoteAudioReadyRef.current = remoteAudioReady;
  }, [remoteAudioReady]);

  useEffect(() => {
    isMutedRef.current = isMuted;
    if (isMuted) {
      setMicLevel(0);
    }
  }, [isMuted]);

  useEffect(() => {
    noiseSuppressionRef.current = noiseSuppression;
  }, [noiseSuppression]);

  useEffect(() => {
    if (callState !== CALL_STATES.IDLE && !activeCall) {
      setCallState(CALL_STATES.IDLE);
    }
  }, [callState, activeCall]);

  useEffect(() => {
    if (callProfiles.selfId) {
      setCallSocketLockOwner(callProfiles.selfId);
    }
  }, [callProfiles.selfId]);

  const stopMicMonitor = useCallback(() => {
    if (micMonitorRef.current) {
      micMonitorRef.current.stop();
      micMonitorRef.current = null;
    }
    setMicLevel(0);
    lastAudioLevelValueRef.current = -1;
  }, []);

  const broadcastAudioLevel = useCallback((level) => {
    const now = Date.now();
    const rounded = Math.round(level * 100) / 100;
    if (now - lastAudioLevelSentRef.current < 50) {
      return;
    }
    lastAudioLevelSentRef.current = now;
    lastAudioLevelValueRef.current = rounded;
    const callId = activeCallRef.current?.call_id;
    if (!callId) {
      return;
    }
    const ws = getCallSignalingSocket(callId);
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return;
    }
    ws.send(JSON.stringify({ type: 'audio_level', level: rounded }));
  }, []);

  const startMicMonitor = useCallback((stream) => {
    stopMicMonitor();
    micMonitorRef.current = createMicLevelMonitor(stream, (level) => {
      const effectiveLevel = isMutedRef.current ? 0 : level;
      setMicLevel(effectiveLevel);
      broadcastAudioLevel(effectiveLevel);
    });
    micMonitorRef.current.start();
  }, [broadcastAudioLevel, stopMicMonitor]);

  const cleanupMedia = useCallback(() => {
    teardownRef.current = true;
    stopMicMonitor();
    if (micPipelineRef.current) {
      try {
        micPipelineRef.current.destroy();
      } catch (_) {
        // ignore
      }
      micPipelineRef.current = null;
      localStreamRef.current = null;
    } else {
      stopMediaStream(localStreamRef.current);
      localStreamRef.current = null;
    }
    stopMediaStream(screenStreamRef.current);
    screenStreamRef.current = null;
    if (screenMixerRef.current) {
      screenMixerRef.current.destroy();
      screenMixerRef.current = null;
    }
    screenAudioMixedRef.current = false;
    setIsSharingScreen(false);
    setScreenShareHasAudio(false);
    setLocalScreenStream(null);
    setRemoteScreenStream(null);
    setRemoteScreenPeerId(null);
    stopMediaStream(remoteStreamRef.current);
    remoteStreamRef.current = null;
    remoteAudioPlayerRef.current?.destroy();
    remoteAudioPlayerRef.current = null;
    closeAllMeshPeers(pcsRef.current, pendingIceByPeerRef.current);
    setRemotePeerStreams({});
    remotePeerStreamsRef.current = {};
    setGroupParticipants([]);
    closePeerConnection(pcRef.current);
    pcRef.current = null;
    pendingIceRef.current = [];
    pendingSignalsRef.current = [];
    localIceCandidatesRef.current = [];
    answerReceivedRef.current = false;
    peerJoinedRef.current = false;
    peerJoinedWaitersRef.current.forEach((resolve) => resolve(false));
    peerJoinedWaitersRef.current = [];
    makingOfferRef.current = false;
    callKindRef.current = 'dm';
    setRemoteAudioReady(false);
    setIsMuted(false);
    setPeerMicLevel(0);
    window.setTimeout(() => {
      teardownRef.current = false;
    }, 0);
  }, [stopMicMonitor]);

  const cleanupCallSocket = useCallback((callId) => {
    const id = callId || activeCallRef.current?.call_id;
    if (id) {
      releaseCallSignalingSocket(id);
    }
  }, []);

  const resetCall = useCallback((reason = 'reset') => {
    logCallEvent('reset', { reason });
    const callId = activeCallRef.current?.call_id;
    cleanupCallSocket(callId);
    cleanupMedia();
    roleRef.current = null;
    if (activeCallRef.current?.call_id) {
      clearCachedIceServers(activeCallRef.current.call_id);
    }
    setActiveCall(null);
    setCallState(CALL_STATES.IDLE);
    setCallProfiles((prev) => ({
      ...prev,
      peerAvatarUrl: null,
      peerName: null,
    }));
  }, [cleanupCallSocket, cleanupMedia]);

  const forceResetCall = useCallback(async (reason = 'watchdog') => {
    logCallEvent('force_reset', { reason, state: callStateRef.current });
    const call = activeCallRef.current;
    const callId = call?.call_id;
    const wasGroup = call?.kind === 'group' || callKindRef.current === 'group';
    resetCall(reason);
    if (callId) {
      try {
        if (wasGroup) {
          await callAPI.leaveRoom(callId);
        } else {
          await callAPI.end(callId);
        }
      } catch (_) {
        // ignore network errors during watchdog cleanup
      }
    }
    callAPI.reset().catch(() => {});
  }, [resetCall]);

  const applyMuteToLocalStream = useCallback((muted) => {
    const pipeline = micPipelineRef.current;
    if (pipeline?.setMicMuted) {
      pipeline.setMicMuted(muted);
      return;
    }
    const stream = localStreamRef.current;
    if (!stream) {
      return;
    }
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
  }, []);

  const toggleMute = useCallback(() => {
    const nextMuted = !isMutedRef.current;
    isMutedRef.current = nextMuted;
    setIsMuted(nextMuted);
    applyMuteToLocalStream(nextMuted);
    if (nextMuted) {
      broadcastAudioLevel(0);
    }
  }, [applyMuteToLocalStream, broadcastAudioLevel]);

  const applyRemoteVideoTrack = useCallback((track, stream, peerId = null) => {
    if (!track || track.kind !== 'video') {
      return;
    }
    const videoStream = stream || new MediaStream([track]);
    setRemoteScreenStream(videoStream);
    setRemoteScreenPeerId(peerId);
    const clearRemote = () => {
      setRemoteScreenStream((current) => (current === videoStream ? null : current));
      setRemoteScreenPeerId((current) => (current === peerId ? null : current));
    };
    track.addEventListener('ended', clearRemote);
    track.addEventListener('mute', () => {
      if (track.readyState === 'ended') {
        clearRemote();
      }
    });
  }, []);

  const listCallPeerConnections = useCallback(() => {
    const items = [];
    if (pcRef.current) {
      items.push({ pc: pcRef.current, peerId: null });
    }
    pcsRef.current.forEach((pc, peerId) => {
      items.push({ pc, peerId });
    });
    return items;
  }, []);

  const attachScreenToPeerConnection = useCallback(async (pc) => {
    const stream = screenStreamRef.current;
    if (!pc || !stream) {
      return false;
    }
    let added = false;
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      const videoAdded = await attachOrReplaceVideoTrack(pc, videoTrack, stream);
      added = added || videoAdded;
    }
    const audioTrack = screenAudioMixedRef.current
      ? null
      : stream.getAudioTracks()[0];
    if (audioTrack) {
      const audioAdded = await attachOrReplaceScreenAudioTrack(
        pc,
        audioTrack,
        stream,
        localStreamRef.current,
      );
      added = added || audioAdded;
    }
    return added;
  }, []);

  const ensureRemoteAudioPlayer = useCallback(() => {
    if (!remoteAudioPlayerRef.current) {
      remoteAudioPlayerRef.current = createRemoteAudioPlayer();
    }
    return remoteAudioPlayerRef.current;
  }, []);

  const changePeerVolume = useCallback((vol) => {
    const v = Math.max(0, Math.min(1, vol));
    setPeerVolume(v);
    remoteAudioPlayerRef.current?.setVolume?.(v);
  }, []);

  const changeScreenVolume = useCallback((vol) => {
    const v = Math.max(0, Math.min(2, vol));
    setScreenVolume(v);
    screenMixerRef.current?.setScreenVolume?.(v);
  }, []);

  const addRemoteAudioTrack = useCallback((track) => {
    if (!track || track.kind !== 'audio') {
      return;
    }
    ensureRemoteAudioPlayer().addTrack(track);
    setRemoteAudioReady(true);
  }, [ensureRemoteAudioPlayer]);

  const renegotiateForScreenShare = useCallback(async () => {
    const call = activeCallRef.current;
    const isGroup = call?.kind === 'group' || callKindRef.current === 'group';
    if (isGroup) {
      const peerIds = Array.from(pcsRef.current.keys());
      await Promise.all(peerIds.map(async (peerId) => {
        for (let attempt = 0; attempt < 8; attempt += 1) {
          const pc = pcsRef.current.get(peerId);
          if (!pc) {
            return;
          }
          if (pc.signalingState !== 'stable') {
            await new Promise((resolve) => window.setTimeout(resolve, 150));
            continue;
          }
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          sendSignalRef.current?.({
            type: 'offer',
            to: peerId,
            sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
          });
          return;
        }
      }));
      return;
    }

    const pc = pcRef.current;
    if (!pc || pc.signalingState !== 'stable') {
      return;
    }
    makingOfferRef.current = true;
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendSignalRef.current?.({
        type: 'offer',
        sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
      });
    } finally {
      makingOfferRef.current = false;
    }
  }, []);

  const restoreMicOutgoingAudio = useCallback(async () => {
    const micTrack = localStreamRef.current?.getAudioTracks?.()?.[0] || null;
    const mixer = screenMixerRef.current;
    screenMixerRef.current = null;
    screenAudioMixedRef.current = false;
    try {
      micPipelineRef.current?.setScreenAudioStream?.(null);
    } catch (_) {
      // ignore
    }
    if (micTrack) {
      try {
        micTrack.contentHint = 'speech';
      } catch (_) {
        // ignore
      }
      await Promise.all(listCallPeerConnections().map(({ pc }) => (
        replaceOutgoingMicTrack(pc, micTrack)
      )));
    }
    mixer?.destroy();
  }, [listCallPeerConnections]);

  const applyMixedOutgoingAudio = useCallback(async (screenStream) => {
    const mixer = createCallAudioMixer(localStreamRef.current, screenStream);
    if (!mixer?.track) {
      logCallEvent('screen_audio_mix', { mixed: false });
      return false;
    }
    const previous = screenMixerRef.current;
    screenMixerRef.current = mixer;
    screenAudioMixedRef.current = true;
    await Promise.all(listCallPeerConnections().map(({ pc }) => (
      replaceOutgoingMicTrack(pc, mixer.track)
    )));
    previous?.destroy();
    logCallEvent('screen_audio_mix', {
      mixed: true,
      peers: listCallPeerConnections().length,
    });
    return true;
  }, [listCallPeerConnections]);

  const stopScreenShare = useCallback(async () => {
    const stream = screenStreamRef.current;
    screenStreamRef.current = null;
    await restoreMicOutgoingAudio();
    setIsSharingScreen(false);
    setScreenShareHasAudio(false);
    setLocalScreenStream(null);
    await Promise.all(listCallPeerConnections().map(({ pc }) => (
      clearScreenSenders(pc, localStreamRef.current)
    )));
    stopMediaStream(stream);
  }, [listCallPeerConnections, restoreMicOutgoingAudio]);

  const startScreenShare = useCallback(async () => {
    if (!canShareScreen() || screenStreamRef.current) {
      return;
    }
    let stream;
    try {
      stream = await acquireScreenShare();
    } catch (error) {
      logCallEvent('screen_share_denied', { message: error?.message || 'denied' });
      return;
    }
    const track = stream.getVideoTracks()[0];
    if (!track) {
      stopMediaStream(stream);
      return;
    }
    const hasAudio = stream.getAudioTracks().length > 0;
    logCallEvent('screen_share_started', { hasAudio });
    screenStreamRef.current = stream;
    setLocalScreenStream(stream);
    setIsSharingScreen(true);
    setScreenShareHasAudio(hasAudio);
    const mixedAudio = hasAudio ? await applyMixedOutgoingAudio(stream) : false;
    screenAudioMixedRef.current = mixedAudio;
    track.addEventListener('ended', () => {
      stopScreenShare();
    });
    stream.addEventListener('addtrack', (event) => {
      if (event.track?.kind !== 'audio' || screenStreamRef.current !== stream) {
        return;
      }
      prepareScreenAudioTrack(event.track);
      setScreenShareHasAudio(true);
      applyMixedOutgoingAudio(stream).catch((error) => {
        logCallEvent('screen_audio_attach_failed', { message: error?.message || 'unknown' });
      });
    });
    let needsOffer = false;
    const peers = listCallPeerConnections();
    for (const { pc } of peers) {
      const added = await attachScreenToPeerConnection(pc);
      if (added) {
        needsOffer = true;
      }
    }
    if (needsOffer || peers.length > 0) {
      await renegotiateForScreenShare();
    }
  }, [
    applyMixedOutgoingAudio,
    attachScreenToPeerConnection,
    listCallPeerConnections,
    renegotiateForScreenShare,
    stopScreenShare,
  ]);

  const updateCallProfiles = useCallback((profiles) => {
    setCallProfiles((prev) => ({ ...prev, ...profiles }));
  }, []);

  const flushPendingSignals = useCallback((callId) => {
    const ws = getCallSignalingSocket(callId);
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return;
    }
    const queued = pendingSignalsRef.current.splice(0, pendingSignalsRef.current.length);
    for (const payload of queued) {
      try {
        ws.send(JSON.stringify(payload));
      } catch (_) {
        // ignore send errors on flush
      }
    }
  }, []);

  const sendSignal = useCallback((payload) => {
    const callId = activeCallRef.current?.call_id;
    if (!callId) {
      return;
    }
    const ws = getCallSignalingSocket(callId);
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return;
    }
    pendingSignalsRef.current.push(payload);
  }, []);

  useEffect(() => {
    sendSignalRef.current = sendSignal;
  }, [sendSignal]);

  const flushPendingIce = useCallback(async (pc) => {
    if (!pc?.remoteDescription || !pendingIceRef.current.length) {
      return;
    }
    const queued = pendingIceRef.current.splice(0, pendingIceRef.current.length);
    for (const candidate of queued) {
      try {
        await pc.addIceCandidate(candidate);
      } catch (_) {
        // ignore stale candidates
      }
    }
  }, []);

  const waitForPeerJoined = useCallback((timeoutMs = PEER_JOIN_WAIT_MS) => {
    if (peerJoinedRef.current) {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      let settled = false;
      const finish = (joined) => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timer);
        peerJoinedWaitersRef.current = peerJoinedWaitersRef.current.filter((item) => item !== finish);
        resolve(joined);
      };
      const timer = window.setTimeout(() => finish(false), timeoutMs);
      peerJoinedWaitersRef.current.push(finish);
    });
  }, []);

  const ensurePeerConnection = useCallback(async (callId) => {
    if (pcRef.current) {
      return pcRef.current;
    }

    let iceServers = getCachedIceServers(callId);
    if (!iceServers.length) {
      const response = await callAPI.getIceServers(callId);
      iceServers = cacheIceServers(callId, normalizeIceServers(response.data));
    }

    const pc = createPeerConnection(iceServers);
    localIceCandidatesRef.current = [];
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const candidate = typeof event.candidate.toJSON === 'function'
          ? event.candidate.toJSON()
          : event.candidate;
        localIceCandidatesRef.current.push(candidate);
        sendSignal({ type: 'ice', candidate });
      }
    };
    pc.ontrack = (event) => {
      if (event.track?.kind === 'video') {
        applyRemoteVideoTrack(event.track, event.streams?.[0] || null, 'peer');
      } else if (event.track?.kind === 'audio') {
        addRemoteAudioTrack(event.track);
      }
      setRemoteAudioReady(true);
      setCallState(CALL_STATES.ACTIVE);
    };
    pc.oniceconnectionstatechange = () => {
      logCallEvent('ice_state', { state: pc.iceConnectionState });
      if (
        pc.iceConnectionState === 'connected'
        || pc.iceConnectionState === 'completed'
      ) {
        setCallState(CALL_STATES.ACTIVE);
      }
      if (pc.iceConnectionState === 'failed' && !teardownRef.current) {
        logCallEvent('ice_failed');
        resetCall('ice_failed');
      }
    };
    pc.onconnectionstatechange = () => {
      logCallEvent('pc_state', { state: pc.connectionState });
      if (pc.connectionState === 'connected') {
        setCallState(CALL_STATES.ACTIVE);
      }
      if (pc.connectionState === 'failed' && !teardownRef.current) {
        logCallEvent('pc_failed');
        resetCall('pc_failed');
      }
    };
    pcRef.current = pc;
    return pc;
  }, [addRemoteAudioTrack, applyRemoteVideoTrack, resetCall, sendSignal]);

  const ensureLocalAudio = useCallback(async (pc) => {
    if (!localStreamRef.current) {
      const { stream, pipeline } = await acquireLocalAudio(noiseSuppressionRef.current);
      micPipelineRef.current = pipeline;
      localStreamRef.current = stream;
      attachLocalAudio(pc, stream);
      startMicMonitor(stream);
    }
    applyMuteToLocalStream(isMutedRef.current);
    await attachScreenToPeerConnection(pc);
  }, [applyMuteToLocalStream, attachScreenToPeerConnection, startMicMonitor]);

  const setNoiseSuppression = useCallback(async (enabled) => {
    const next = Boolean(enabled);
    setNoiseSuppressionState(next);
    writeNoiseSuppressionPreference(next);
    noiseSuppressionRef.current = next;

    const pipeline = micPipelineRef.current;
    if (!pipeline) {
      return;
    }
    const ok = await pipeline.setNoiseSuppression(next);
    if (!ok) {
      logCallEvent('noise_suppression_apply_failed');
    }
  }, []);

  const ensureLocalMic = useCallback(async () => {
    if (!localStreamRef.current) {
      const { stream, pipeline } = await acquireLocalAudio(noiseSuppressionRef.current);
      micPipelineRef.current = pipeline;
      localStreamRef.current = stream;
      startMicMonitor(stream);
    }
    applyMuteToLocalStream(isMutedRef.current);
    return localStreamRef.current;
  }, [applyMuteToLocalStream, startMicMonitor]);

  const upsertRemotePeerStream = useCallback((peerId, stream) => {
    if (!stream) {
      return;
    }
    const existing = remotePeerStreamsRef.current[peerId];
    if (existing && existing !== stream) {
      stream.getAudioTracks().forEach((track) => {
        if (!existing.getTrackById?.(track.id)) {
          existing.addTrack(track);
        }
      });
    } else {
      remotePeerStreamsRef.current = {
        ...remotePeerStreamsRef.current,
        [peerId]: stream,
      };
    }
    setRemotePeerStreams({ ...remotePeerStreamsRef.current });
    setRemoteAudioReady(true);
    setCallState(CALL_STATES.ACTIVE);
  }, []);

  const removeRemotePeerStream = useCallback((peerId) => {
    const next = { ...remotePeerStreamsRef.current };
    delete next[peerId];
    remotePeerStreamsRef.current = next;
    setRemotePeerStreams(next);
  }, []);

  const flushPeerPendingIce = useCallback(async (peerId, pc) => {
    if (!pc?.remoteDescription) {
      return;
    }
    const queued = pendingIceByPeerRef.current.get(peerId) || [];
    pendingIceByPeerRef.current.set(peerId, []);
    for (const candidate of queued) {
      try {
        await pc.addIceCandidate(candidate);
      } catch (_) {
        // ignore stale candidates
      }
    }
  }, []);

  const ensureMeshPeerConnection = useCallback(async (peerId) => {
    const existing = pcsRef.current.get(peerId);
    if (existing) {
      return existing;
    }
    const call = activeCallRef.current;
    if (!call?.call_id) {
      throw new Error('no active group room');
    }
    let iceServers = getCachedIceServers(call.call_id);
    if (!iceServers.length) {
      const response = await callAPI.getIceServers(call.call_id);
      iceServers = cacheIceServers(call.call_id, normalizeIceServers(response.data));
    }
    const pc = createPeerConnection(iceServers);
    const stream = await ensureLocalMic();
    attachStreamTracks(pc, stream);
    await attachScreenToPeerConnection(pc);
    pc.onicecandidate = (event) => {
      if (!event.candidate) {
        return;
      }
      const candidate = typeof event.candidate.toJSON === 'function'
        ? event.candidate.toJSON()
        : event.candidate;
      sendSignalRef.current?.({ type: 'ice', to: peerId, candidate });
    };
    pc.ontrack = (event) => {
      if (event.track?.kind === 'video') {
        applyRemoteVideoTrack(event.track, event.streams?.[0] || null, peerId);
      } else if (event.track?.kind === 'audio') {
        addRemoteAudioTrack(event.track);
      }
      const [remoteStream] = event.streams;
      if (!remoteStream) {
        return;
      }
      upsertRemotePeerStream(peerId, remoteStream);
    };
    pc.onconnectionstatechange = () => {
      logCallEvent('mesh_pc_state', { peerId, state: pc.connectionState });
      if (pc.connectionState === 'connected') {
        setCallState(CALL_STATES.ACTIVE);
      }
    };
    pcsRef.current.set(peerId, pc);
    return pc;
  }, [addRemoteAudioTrack, applyRemoteVideoTrack, attachScreenToPeerConnection, ensureLocalMic, upsertRemotePeerStream]);

  const negotiateMeshOffer = useCallback(async (peerId) => {
    const pc = await ensureMeshPeerConnection(peerId);
    if (pc.signalingState !== 'stable') {
      return;
    }
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    sendSignalRef.current?.({
      type: 'offer',
      to: peerId,
      sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
    });
  }, [ensureMeshPeerConnection]);

  const processGroupSignalMessage = useCallback(async (message) => {
    const peerId = message.from || message.user_id;
    if (message.type === 'audio_level') {
      setPeerMicLevel(typeof message.level === 'number' ? message.level : 0);
      return;
    }
    if (message.type === 'peer_left' && peerId) {
      logCallEvent('mesh_peer_left', { peerId });
      closeMeshPeer(pcsRef.current, pendingIceByPeerRef.current, peerId);
      removeRemotePeerStream(peerId);
      setRemoteScreenPeerId((current) => {
        if (current === peerId) {
          setRemoteScreenStream(null);
          return null;
        }
        return current;
      });
      setGroupParticipants((prev) => prev.filter((item) => item.user_id !== peerId));
      return;
    }
    if (message.type === 'peer_joined') {
      // New joiner sends offers; existing peers wait.
      logCallEvent('mesh_peer_joined', { peerId: message.user_id });
      return;
    }
    if (!peerId || peerId === callProfiles.selfId) {
      return;
    }

    console.info('[call] mesh signal in:', message.type, 'from', peerId);
    const pc = await ensureMeshPeerConnection(peerId);

    if (message.type === 'offer') {
      const remote = toSessionDescription(message.sdp, 'offer');
      if (!remote) {
        return;
      }
      try {
        await pc.setRemoteDescription(remote);
      } catch (error) {
        logCallEvent('mesh_offer_failed', { peerId, message: error?.message });
        return;
      }
      await flushPeerPendingIce(peerId, pc);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendSignalRef.current?.({
        type: 'answer',
        to: peerId,
        sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
      });
      setCallState(CALL_STATES.CONNECTING);
      return;
    }

    if (message.type === 'answer') {
      if (pc.signalingState !== 'have-local-offer' && pc.remoteDescription) {
        return;
      }
      const remote = toSessionDescription(message.sdp, 'answer');
      if (!remote) {
        return;
      }
      await pc.setRemoteDescription(remote);
      await flushPeerPendingIce(peerId, pc);
      setCallState(CALL_STATES.CONNECTING);
      return;
    }

    if (message.type === 'ice' && message.candidate) {
      if (!pc.remoteDescription) {
        const queue = pendingIceByPeerRef.current.get(peerId) || [];
        queue.push(message.candidate);
        pendingIceByPeerRef.current.set(peerId, queue);
        return;
      }
      try {
        await pc.addIceCandidate(message.candidate);
      } catch (_) {
        // ignore
      }
    }
  }, [
    callProfiles.selfId,
    ensureMeshPeerConnection,
    flushPeerPendingIce,
    removeRemotePeerStream,
  ]);

  const processSignalMessage = useCallback(async (message) => {
    const call = activeCallRef.current;
    if (!call?.call_id) return;

    if (call.kind === 'group' || callKindRef.current === 'group') {
      await processGroupSignalMessage(message);
      return;
    }

    if (message.type === 'audio_level') {
      setPeerMicLevel(typeof message.level === 'number' ? message.level : 0);
      return;
    }

    if (message.type === 'peer_joined') {
      logCallEvent('peer_joined', { userId: message.user_id });
      peerJoinedRef.current = true;
      const waiters = peerJoinedWaitersRef.current.splice(0, peerJoinedWaitersRef.current.length);
      waiters.forEach((resolve) => resolve(true));
      // Re-send gathered ICE for the peer that just connected.
      if (localIceCandidatesRef.current.length) {
        localIceCandidatesRef.current.forEach((candidate) => {
          sendSignal({ type: 'ice', candidate });
        });
      }
      return;
    }

    console.info('[call] signal in:', message.type);
    const pc = await ensurePeerConnection(call.call_id);

    if (message.type === 'offer') {
      if (makingOfferRef.current) {
        logCallEvent('offer_ignored_while_making_offer');
        return;
      }
      const remote = toSessionDescription(message.sdp, 'offer');
      if (!remote) {
        logCallEvent('offer_invalid_sdp');
        return;
      }
      await ensureLocalAudio(pc);
      await pc.setRemoteDescription(remote);
      await flushPendingIce(pc);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendSignal({
        type: 'answer',
        sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
      });
      setCallState(CALL_STATES.CONNECTING);
      return;
    }

    if (message.type === 'answer') {
      if (pc.signalingState !== 'have-local-offer' && pc.remoteDescription) {
        logCallEvent('answer_ignored', { state: pc.signalingState });
        return;
      }
      const remote = toSessionDescription(message.sdp, 'answer');
      if (!remote) {
        logCallEvent('answer_invalid_sdp');
        return;
      }
      await pc.setRemoteDescription(remote);
      answerReceivedRef.current = true;
      await flushPendingIce(pc);
      setCallState(CALL_STATES.CONNECTING);
      return;
    }

    if (message.type === 'ice' && message.candidate) {
      if (!pc.remoteDescription) {
        pendingIceRef.current.push(message.candidate);
        return;
      }
      try {
        await pc.addIceCandidate(message.candidate);
      } catch (_) {
        // ignore late candidates
      }
    }
  }, [
    ensureLocalAudio,
    ensurePeerConnection,
    flushPendingIce,
    processGroupSignalMessage,
    sendSignal,
  ]);

  const handleSignalMessage = useCallback((message) => {
    signalChainRef.current = signalChainRef.current
      .then(() => processSignalMessage(message))
      .catch((error) => {
        logCallEvent('signal_handler_error', { message: error?.message || 'unknown' });
      });
    return signalChainRef.current;
  }, [processSignalMessage]);

  useEffect(() => {
    handleSignalMessageRef.current = handleSignalMessage;
  }, [handleSignalMessage]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    window.__lynxCallSignalHandler = (message) => {
      handleSignalMessageRef.current?.(message);
    };
    return () => {
      window.__lynxCallSignalHandler = null;
    };
  }, []);

  const openCallSocket = useCallback(async (callId) => {
    const callIdStr = String(callId);
    logCallEvent('signaling_ws_opening', { callId: callIdStr });
    try {
      const ws = await acquireCallSignalingSocket(callId, {
        onClose: (event) => {
          if (isDuplicateCallSocketClose(event) || isRateLimitedCallSocketClose(event)) {
            logCallEvent('signaling_ws_rejected', {
              callId: callIdStr,
              code: event.code,
              reason: event.reason || 'rejected',
            });
            return;
          }
          logCallEvent('signaling_ws_closed', {
            callId: callIdStr,
            code: event.code,
            reason: event.reason || 'closed',
            wasClean: event.wasClean,
          });
        },
      });
      logCallEvent('signaling_ws_open', { callId: callIdStr });
      flushPendingSignals(callIdStr);
      return ws;
    } catch (error) {
      logCallEvent('signaling_ws_failed', {
        callId: callIdStr,
        message: error?.message || 'unknown',
      });
      throw error;
    }
  }, [flushPendingSignals]);

  const enterGroupRoom = useCallback(async (room) => {
    const roomId = room.room_id || room.call_id;
    const selfId = callProfiles.selfId;
    const participants = room.participants || [];
    callKindRef.current = 'group';
    roleRef.current = 'group';
    setGroupParticipants(participants);
    setActiveCall({
      call_id: roomId,
      chat_id: room.chat_id,
      kind: 'group',
      host_id: room.host_id,
      peer_name: room.title || room.chat_title || 'group',
      role: 'group',
    });
    setCallState(CALL_STATES.CONNECTING);
    resetCallSocketGuard(roomId);
    await ensureLocalMic();
    await openCallSocket(roomId);

    const peers = participants
      .map((item) => item.user_id)
      .filter((id) => id && id !== selfId);
    await Promise.all(peers.map((peerId) => negotiateMeshOffer(peerId)));
    if (peers.length === 0) {
      setCallState(CALL_STATES.ACTIVE);
    }
  }, [callProfiles.selfId, ensureLocalMic, negotiateMeshOffer, openCallSocket]);

  const startGroupRoom = useCallback(async (chatId) => {
    if (callStateRef.current !== CALL_STATES.IDLE) {
      return;
    }
    const response = await callAPI.createRoom(chatId);
    const room = response.data;
    setActiveGroupRooms((prev) => ({
      ...prev,
      [room.chat_id]: room,
    }));
    await enterGroupRoom(room);
  }, [enterGroupRoom]);

  const joinGroupRoom = useCallback(async (roomId) => {
    if (callStateRef.current !== CALL_STATES.IDLE) {
      return;
    }
    const response = await callAPI.joinRoom(roomId);
    const room = response.data;
    setActiveGroupRooms((prev) => ({
      ...prev,
      [room.chat_id]: room,
    }));
    await enterGroupRoom(room);
  }, [enterGroupRoom]);

  const leaveGroupRoom = useCallback(async () => {
    const call = activeCallRef.current;
    const roomId = call?.call_id;
    resetCall('leave_group');
    if (!roomId) {
      return;
    }
    try {
      await callAPI.leaveRoom(roomId);
    } catch (_) {
      try {
        await callAPI.reset();
      } catch (_) {
        // ignore
      }
    }
  }, [resetCall]);

  const terminateGroupRoom = useCallback(async () => {
    const call = activeCallRef.current;
    const roomId = call?.call_id;
    resetCall('terminate_group');
    if (!roomId) {
      return;
    }
    try {
      await callAPI.terminateRoom(roomId);
    } catch (_) {
      try {
        await callAPI.reset();
      } catch (_) {
        // ignore
      }
    }
  }, [resetCall]);

  const sendOfferWithRetry = useCallback(async (pc) => {
    const payload = {
      type: 'offer',
      sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
    };
    for (let attempt = 0; attempt < OFFER_RETRY_COUNT; attempt += 1) {
      if (answerReceivedRef.current || teardownRef.current) {
        return;
      }
      sendSignal(payload);
      if (attempt < OFFER_RETRY_COUNT - 1) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, OFFER_RETRY_MS);
        });
      }
    }
  }, [sendSignal]);

  const startCallerNegotiation = useCallback(async (callId) => {
    if (negotiatingRef.current) {
      return;
    }
    negotiatingRef.current = true;
    makingOfferRef.current = true;
    answerReceivedRef.current = false;
    try {
      await withTimeout(
        openCallSocket(callId),
        NEGOTIATION_TIMEOUT_MS,
        'call signaling timed out',
      );
      const joined = await waitForPeerJoined(PEER_JOIN_WAIT_MS);
      logCallEvent('peer_join_wait_done', { joined });
      const pc = await withTimeout(
        ensurePeerConnection(callId),
        NEGOTIATION_TIMEOUT_MS,
        'call setup timed out',
      );
      await ensureLocalAudio(pc);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await sendOfferWithRetry(pc);
      setCallState(CALL_STATES.CONNECTING);
    } finally {
      makingOfferRef.current = false;
      negotiatingRef.current = false;
    }
  }, [ensureLocalAudio, ensurePeerConnection, openCallSocket, sendOfferWithRetry, waitForPeerJoined]);

  const endCall = useCallback(async () => {
    const call = activeCallRef.current;
    const callId = call?.call_id;
    if (call?.kind === 'group' || callKindRef.current === 'group') {
      await leaveGroupRoom();
      return;
    }
    resetCall();
    if (!callId) {
      return;
    }
    try {
      await callAPI.end(callId);
    } catch (_) {
      try {
        await callAPI.reset();
      } catch (_) {
        // ignore network errors on hangup
      }
    }
  }, [leaveGroupRoom, resetCall]);

  const startCall = useCallback(async (chatId, { retried = false } = {}) => {
    if (!retried && callStateRef.current !== CALL_STATES.IDLE) {
      return;
    }
    let createdCallId = null;
    try {
      const response = await callAPI.create(chatId);
      const data = response.data;
      createdCallId = data.call_id;
      roleRef.current = 'caller';
      setActiveCall({
        call_id: data.call_id,
        chat_id: data.chat_id,
        peer_id: data.peer_id,
        peer_name: data.peer_name,
        role: 'caller',
      });
      setCallState(CALL_STATES.OUTGOING);
      const callId = data.call_id;
      if (preconnectingRef.current !== callId) {
        preconnectingRef.current = callId;
        openCallSocket(callId).catch((error) => {
          logCallEvent('caller_preconnect_failed', { message: error?.message || 'unknown' });
        }).finally(() => {
          if (preconnectingRef.current === callId) {
            preconnectingRef.current = null;
          }
        });
      }
    } catch (error) {
      const payload = error?.response?.data || {};
      const detail = typeof payload.detail === 'string' ? payload.detail : '';
      const msg = typeof payload.msg === 'string' ? payload.msg : '';
      const busy =
        error?.response?.status === 409 ||
        detail.includes('already in a call') ||
        msg === 'busy' ||
        msg === 'peer busy';
      if (!retried && busy) {
        try {
          await callAPI.reset();
          resetCall();
          return startCall(chatId, { retried: true });
        } catch (_) {
          // fall through to normal error handling
        }
      }
      if (createdCallId) {
        try {
          await callAPI.end(createdCallId);
        } catch (_) {
          // ignore cleanup errors
        }
      }
      resetCall();
      throw error;
    }
  }, [openCallSocket, resetCall]);

  const acceptCall = useCallback(async () => {
    if (acceptingRef.current) {
      return;
    }
    const call = activeCallRef.current;
    if (!call?.call_id || callStateRef.current !== CALL_STATES.INCOMING) {
      return;
    }
    acceptingRef.current = true;
    roleRef.current = 'callee';
    setCallState(CALL_STATES.CONNECTING);
    resetCallSocketGuard(call.call_id);
    try {
      const response = await callAPI.accept(call.call_id);
      const data = response.data;
      setActiveCall((prev) => ({
        ...(prev || call),
        ...data,
        call_id: call.call_id,
        role: 'callee',
      }));
      try {
        await openCallSocket(call.call_id);
      } catch (wsError) {
        logCallEvent('signaling_ws_failed_accept', { message: wsError?.message || 'unknown' });
        const callId = call.call_id;
        window.setTimeout(() => {
          if (callStateRef.current !== CALL_STATES.CONNECTING
            || activeCallRef.current?.call_id !== callId) {
            return;
          }
          openCallSocket(callId).catch(() => {});
        }, 3000);
      }
    } catch (error) {
      resetCall();
      throw error;
    } finally {
      acceptingRef.current = false;
    }
  }, [openCallSocket, resetCall]);

  const rejectCall = useCallback(async () => {
    const call = activeCallRef.current;
    resetCall();
    if (!call?.call_id) {
      return;
    }
    try {
      await callAPI.reject(call.call_id);
    } catch (_) {
      try {
        await callAPI.end(call.call_id);
      } catch (_) {
        // ignore
      }
    }
    try {
      await callAPI.reset();
    } catch (_) {
      // ignore
    }
  }, [resetCall]);

  const startCallerNegotiationRef = useRef(startCallerNegotiation);
  const endCallRef = useRef(endCall);
  const resetCallRef = useRef(resetCall);

  useEffect(() => {
    startCallerNegotiationRef.current = startCallerNegotiation;
    endCallRef.current = endCall;
    resetCallRef.current = resetCall;
  }, [endCall, resetCall, startCallerNegotiation]);

  useEffect(() => {
    const unsubIncoming = subscribe('call_incoming', (event) => {
      if (callStateRef.current !== CALL_STATES.IDLE) return;
      const data = event.data || {};
      resetCallSocketGuard(data.call_id);
      setActiveCall({
        call_id: data.call_id,
        chat_id: data.chat_id,
        peer_id: data.caller_id,
        peer_name: data.caller_name,
        role: 'callee',
      });
      setCallState(CALL_STATES.INCOMING);
    });

    const unsubAccepted = subscribe('call_accepted', async (event) => {
      const data = event.data || {};
      const call = activeCallRef.current;
      if (!call || String(call.call_id) !== String(data.call_id)) return;
      if (roleRef.current !== 'caller') return;
      console.info('[call] call_accepted received, starting negotiation');
      logCallEvent('call_accepted', { callId: call.call_id });
      setCallState(CALL_STATES.CONNECTING);
      try {
        await startCallerNegotiationRef.current(call.call_id);
      } catch (error) {
        logCallEvent('negotiation_failed', { message: error?.message || 'unknown' });
      }
    });

    const unsubRejected = subscribe('call_rejected', (event) => {
      const data = event.data || {};
      const call = activeCallRef.current;
      if (call && String(call.call_id) === String(data.call_id)) {
        resetCallRef.current();
      }
    });

    const unsubEnded = subscribe('call_ended', (event) => {
      const data = event.data || {};
      const call = activeCallRef.current;
      if (!call || String(call.call_id) !== String(data.call_id)) {
        return;
      }
      logCallEvent('system_call_ended', { reason: data.reason || 'ended' });
      resetCallRef.current('peer_ended');
    });

    const unsubMissed = subscribe('call_missed', (event) => {
      const data = event.data || {};
      const call = activeCallRef.current;
      if (call && String(call.call_id) === String(data.call_id)) {
        resetCallRef.current();
        return;
      }
      if (callStateRef.current === CALL_STATES.INCOMING) {
        resetCallRef.current();
      }
    });

    const unsubGroupStarted = subscribe('group_call_started', (event) => {
      const data = event.data || {};
      if (!data.chat_id || !data.room_id) {
        return;
      }
      setActiveGroupRooms((prev) => ({
        ...prev,
        [data.chat_id]: {
          room_id: data.room_id,
          call_id: data.room_id,
          chat_id: data.chat_id,
          host_id: data.host_id,
          participants: data.participants || [],
          kind: 'group',
          state: 'open',
        },
      }));
    });

    const unsubGroupUpdated = subscribe('group_call_updated', (event) => {
      const data = event.data || {};
      if (!data.chat_id || !data.room_id) {
        return;
      }
      setActiveGroupRooms((prev) => ({
        ...prev,
        [data.chat_id]: {
          room_id: data.room_id,
          call_id: data.room_id,
          chat_id: data.chat_id,
          host_id: data.host_id,
          participants: data.participants || [],
          kind: 'group',
          state: 'open',
        },
      }));
      const call = activeCallRef.current;
      if (call?.kind === 'group' && String(call.call_id) === String(data.room_id)) {
        setGroupParticipants(data.participants || []);
        if (data.host_id && data.host_id !== call.host_id) {
          setActiveCall((prev) => prev ? { ...prev, host_id: data.host_id } : prev);
        }
      }
    });

    const unsubGroupEnded = subscribe('group_call_ended', (event) => {
      const data = event.data || {};
      if (data.chat_id) {
        setActiveGroupRooms((prev) => {
          const next = { ...prev };
          delete next[data.chat_id];
          return next;
        });
      }
      const call = activeCallRef.current;
      if (call?.kind === 'group' && String(call.call_id) === String(data.room_id)) {
        resetCallRef.current('group_call_ended');
      }
    });

    return () => {
      unsubIncoming();
      unsubAccepted();
      unsubRejected();
      unsubEnded();
      unsubMissed();
      unsubGroupStarted();
      unsubGroupUpdated();
      unsubGroupEnded();
    };
  }, [subscribe]);

  useEffect(() => {
    if (callState === CALL_STATES.IDLE) {
      return undefined;
    }
    if (callKindRef.current === 'group' || activeCallRef.current?.kind === 'group') {
      if (
        callState === CALL_STATES.ACTIVE
        || remoteAudioReady
        || anyMeshConnected(pcsRef.current)
      ) {
        return undefined;
      }
    }
    if (
      callState === CALL_STATES.ACTIVE
      || remoteAudioReady
      || isMediaConnected(pcRef.current)
    ) {
      return undefined;
    }
    if (callState === CALL_STATES.INCOMING) {
      return undefined;
    }

    const timeoutMs = callState === CALL_STATES.OUTGOING
      ? OUTGOING_RING_TIMEOUT_MS
      : NEGOTIATION_TIMEOUT_MS;

    const timer = window.setTimeout(() => {
      if (callKindRef.current === 'group' || activeCallRef.current?.kind === 'group') {
        if (
          callStateRef.current === CALL_STATES.ACTIVE
          || remoteAudioReadyRef.current
          || anyMeshConnected(pcsRef.current)
        ) {
          return;
        }
      }
      if (
        callStateRef.current === CALL_STATES.ACTIVE
        || remoteAudioReadyRef.current
        || isMediaConnected(pcRef.current)
      ) {
        return;
      }
      forceResetCall(callState === CALL_STATES.OUTGOING ? 'ring_timeout' : 'negotiation_timeout');
    }, timeoutMs);

    return () => window.clearTimeout(timer);
  }, [callState, remoteAudioReady, forceResetCall]);

  useEffect(() => {
    if (peerMicLevel <= 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setPeerMicLevel((level) => (level > 0 ? Math.max(0, level - 0.12) : 0));
    }, 90);
    return () => window.clearTimeout(timer);
  }, [peerMicLevel]);

  useEffect(() => () => {
    cleanupMedia();
  }, [cleanupMedia]);

  const value = {
    callState,
    activeCall,
    callProfiles,
    updateCallProfiles,
    remoteAudioReady,
    isMuted,
    noiseSuppression,
    setNoiseSuppression,
    micLevel,
    peerMicLevel,
    toggleMute,
    canShareScreen: canShareScreen(),
    isSharingScreen,
    screenShareHasAudio,
    localScreenStream,
    remoteScreenStream,
    remoteScreenPeerId,
    startScreenShare,
    stopScreenShare,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    forceResetCall,
    startGroupRoom,
    joinGroupRoom,
    leaveGroupRoom,
    terminateGroupRoom,
    activeGroupRooms,
    groupParticipants,
    setActiveGroupRooms,
    isInCall: callState !== CALL_STATES.IDLE,
    isIncoming: callState === CALL_STATES.INCOMING,
    isActive: callState === CALL_STATES.ACTIVE || callState === CALL_STATES.CONNECTING,
    peerVolume,
    changePeerVolume,
    screenVolume,
    changeScreenVolume,
    isGroupCall: Boolean(activeCall?.kind === 'group'),
    isGroupHost: Boolean(activeCall?.kind === 'group' && activeCall?.host_id === callProfiles.selfId),
  };

  return (
    <VoiceCallContext.Provider value={value}>
      {children}
    </VoiceCallContext.Provider>
  );
};

export const useVoiceCall = () => {
  const context = useContext(VoiceCallContext);
  if (!context) {
    throw new Error('useVoiceCall must be used within VoiceCallProvider');
  }
  return context;
};
